// ============================================================================
//  RemoteServersDialog — the other servers Database Tools can read from
// ============================================================================
//
//  Add a server by its address, port, username and password. The server tests
//  the connection BEFORE it saves anything, and says in plain words what is
//  wrong when it cannot get in — almost always something fixed in the other
//  server's cPanel, which is where the messages point.
//
//  The password is sent once, when saved, and never comes back: the list shows
//  every saved detail except that one. Editing without retyping it keeps the
//  saved one.
import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, TextField,
  Typography, Alert, IconButton, Tooltip, Checkbox, FormControlLabel, Divider,
  CircularProgress, InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DnsIcon from '@mui/icons-material/Dns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { dormancyServerSave, dormancyServerTest, dormancyServerDelete } from '../BaseURL';

const PRIMARY = '#1A9B7F';
const INK = '#0F172A';
const MUTED = '#64748B';
const FAINT = '#94A3B8';
const LINE = '#E7EBF0';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const BLANK = { id: 0, label: '', host: '', port: 3306, db_user: '', password: '', use_ssl: true };

export default function RemoteServersDialog({ open, onClose, apiKey, servers, onChanged, me }) {
  const [form, setForm] = useState(null);          // null = the list; object = editing
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(null);           // 'save' | `test-${id}` | `del-${id}`
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const set = (k) => (e) => setForm((f) => ({
    ...f, [k]: e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e,
  }));

  const save = async (e) => {
    if (e) e.preventDefault();
    setBusy('save'); setError(null); setNotice(null);
    try {
      const { data } = await axios.post(dormancyServerSave, {
        key: apiKey, id: form.id || undefined,
        label: form.label, host: form.host, port: Number(form.port) || 3306,
        db_user: form.db_user, password: form.password || undefined,
        use_ssl: !!form.use_ssl, user: me,
      });
      setNotice(`Connected and saved — ${data.version || 'the server'} answered, `
        + `${data.databases} database${data.databases === 1 ? '' : 's'} visible to that user.`);
      setForm(null);
      await onChanged();
    } catch (err) {
      //  The server's own words: they name the fix.
      setError(err?.response?.data?.message || err.message);
    } finally { setBusy(null); }
  };

  const test = async (s) => {
    setBusy(`test-${s.id}`); setError(null); setNotice(null);
    try {
      const { data } = await axios.post(dormancyServerTest, { key: apiKey, id: s.id });
      setNotice(`${s.label}: connected — ${data.databases} database${data.databases === 1 ? '' : 's'} visible.`);
    } catch (err) {
      setError(`${s.label}: ${err?.response?.data?.message || err.message}`);
    } finally {
      setBusy(null);
      await onChanged();
    }
  };

  const remove = async (s) => {
    //  One confirm: removing a saved server deletes nothing on that server, only
    //  this system's way of reaching it.
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove "${s.label}"? Nothing on that server is touched — `
      + 'only the saved connection here.')) return;
    setBusy(`del-${s.id}`); setError(null); setNotice(null);
    try {
      await axios.post(dormancyServerDelete, { key: apiKey, id: s.id });
      await onChanged();
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally { setBusy(null); }
  };

  const close = () => { setForm(null); setError(null); setNotice(null); onClose(); };
  const editing = !!form;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pr: 6 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: 2, display: 'grid', placeItems: 'center',
          background: `${PRIMARY}14`, color: PRIMARY,
        }}><DnsIcon sx={{ fontSize: 19 }} /></Box>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: INK, lineHeight: 1.2 }}>
            {editing ? (form.id ? `Edit ${form.label || 'server'}` : 'Add a server') : 'Other servers'}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: MUTED }}>
            {editing ? 'Tested before it is saved' : 'Facility databases on other machines'}
          </Typography>
        </Box>
        <IconButton onClick={close} aria-label="Close"
          sx={{ position: 'absolute', right: 10, top: 10 }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setError(null)}>{error}</Alert>
        )}
        {notice && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setNotice(null)}>{notice}</Alert>
        )}

        {/* ── the list ────────────────────────────────────────────────────── */}
        {!editing && (
          <>
            {servers.length === 0 && (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>
                  No other servers yet
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: MUTED, mt: .5 }}>
                  Add one to read its facility databases from here.
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'grid', gap: 1 }}>
              {servers.map((s) => {
                const ok = !!s.last_ok_at && !s.last_error;
                return (
                  <Box key={s.id} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5,
                    border: `1px solid ${LINE}`, borderRadius: 2,
                    boxShadow: `inset 3px 0 0 ${ok ? '#15803D' : s.last_error ? '#B91C1C' : FAINT}`,
                  }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: INK }}>{s.label}</Typography>
                      <Typography noWrap sx={{ fontSize: 11.5, color: MUTED, fontFamily: MONO }}>
                        {s.db_user}@{s.host}:{s.port}{s.use_ssl ? ' · encrypted' : ''}
                      </Typography>
                      {s.last_error && (
                        <Typography sx={{ fontSize: 11, color: '#B91C1C', mt: .25 }}>{s.last_error}</Typography>
                      )}
                    </Box>
                    <Tooltip title="Test the connection">
                      <span>
                        <IconButton size="small" onClick={() => test(s)} disabled={!!busy}>
                          {busy === `test-${s.id}` ? <CircularProgress size={16} />
                            : <NetworkCheckIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <span>
                        <IconButton size="small" disabled={!!busy}
                          onClick={() => { setError(null); setNotice(null); setForm({ ...BLANK, ...s, password: '', use_ssl: !!s.use_ssl }); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <span>
                        <IconButton size="small" onClick={() => remove(s)} disabled={!!busy}
                          sx={{ '&:hover': { color: '#B91C1C' } }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: MUTED, letterSpacing: .5,
              textTransform: 'uppercase', mb: .75 }}>Before adding one — on the OTHER server</Typography>
            <Box component="ol" sx={{ m: 0, pl: 2.5, color: MUTED, fontSize: 12.5, lineHeight: 1.7 }}>
              <li>cPanel ▸ <strong>MySQL Databases</strong>: create a user and give it only the
                <strong> SELECT</strong> permission on the facility database.</li>
              <li>cPanel ▸ <strong>Remote MySQL</strong>: add <strong>this</strong> server's IP address.
                Only that one address — never <code>%</code>.</li>
            </Box>
          </>
        )}

        {/* ── add / edit ──────────────────────────────────────────────────── */}
        {editing && (
          <Box component="form" id="remote-server-form" onSubmit={save} sx={{ display: 'grid', gap: 1.75 }}>
            <TextField size="small" label="Name" placeholder="e.g. Gina Maternity"
              value={form.label} onChange={set('label')} autoFocus required />
            <Box sx={{ display: 'flex', gap: 1.25 }}>
              <TextField size="small" label="Host or IP address" placeholder="102.176.x.x"
                value={form.host} onChange={set('host')} required sx={{ flex: 1 }}
                inputProps={{ style: { fontFamily: MONO } }} />
              <TextField size="small" label="Port" type="number" value={form.port}
                onChange={set('port')} sx={{ width: 110 }} inputProps={{ min: 1, max: 65535 }} />
            </Box>
            <TextField size="small" label="Database username" value={form.db_user}
              onChange={set('db_user')} required autoComplete="off"
              inputProps={{ style: { fontFamily: MONO } }} />
            <TextField size="small" label="Password" type={showPass ? 'text' : 'password'}
              value={form.password} onChange={set('password')}
              required={!form.id} autoComplete="new-password"
              helperText={form.id ? 'Leave blank to keep the saved password.' : 'Stored encrypted. Never shown again.'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" edge="end" onClick={() => setShowPass((v) => !v)}
                      aria-label={showPass ? 'Hide password' : 'Show password'}>
                      {showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
            <FormControlLabel
              control={<Checkbox size="small" checked={!!form.use_ssl} onChange={set('use_ssl')} />}
              label={<Typography sx={{ fontSize: 13 }}>Encrypt the connection
                <Typography component="span" sx={{ fontSize: 11.5, color: FAINT, ml: .75 }}>
                  (untick only if that server says it does not support it)
                </Typography></Typography>} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {!editing ? (
          <>
            <Button onClick={close} sx={{ color: MUTED, textTransform: 'none' }}>Close</Button>
            <Button variant="contained" disableElevation startIcon={<AddIcon />}
              onClick={() => { setError(null); setNotice(null); setForm({ ...BLANK }); }}
              sx={{ background: PRIMARY, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
              Add a server
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => { setForm(null); setError(null); }} disabled={busy === 'save'}
              sx={{ color: MUTED, textTransform: 'none' }}>Back</Button>
            <Button type="submit" form="remote-server-form" variant="contained" disableElevation
              disabled={busy === 'save'}
              startIcon={busy === 'save' ? <CircularProgress size={15} color="inherit" /> : <NetworkCheckIcon />}
              sx={{ background: PRIMARY, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
              {busy === 'save' ? 'Connecting…' : 'Test and save'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
